import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react'
import { IMessage } from '@stomp/stompjs'

import { getObjectCodeUrl } from '@pages/ConfiguredEntity/helpers'

import { useExecuteCommandMutation } from '@redux/api'

import { useSubscription } from '@hooks'
import { EVENT_NAME_OBJECT_MESSAGE } from '@constants'
import { MapPointGroup, ObjectDataRecord, parseMessageBody } from '@types'

import type { ObjectModelInfo } from './types'

/**
 * Хук для WebSocket подписки на изменения точек на карте
 */
export const useMapPointsSubscription = (
  isMapReady: boolean,
  pointGroups: MapPointGroup[],
  setPointsData: Dispatch<SetStateAction<Map<string, ObjectDataRecord[]>>>,
  objectModelsMap: Map<string, ObjectModelInfo>
) => {
  const [executeCommand] = useExecuteCommandMutation()

  // Уникальные objectCode
  const uniqueObjectCodes = useMemo(
    () => [...new Set(pointGroups.map(g => g.objectCode))].sort(),
    [pointGroups]
  )

  // Для подписки: ['/obj_code/code1', '/obj_code/code2', ...]
  const destinations = useMemo(
    () => uniqueObjectCodes.map(code => getObjectCodeUrl(code)),
    [uniqueObjectCodes]
  )

  // Объект Map<objectCode, MapPointGroup[]> для быстрого поиска групп
  const groupsByObjectCode = useMemo(() => {
    const map = new Map<string, MapPointGroup[]>()
    pointGroups.forEach(group => {
      const groups = map.get(group.objectCode) || []
      groups.push(group)
      map.set(group.objectCode, groups)
    })

    return map
  }, [pointGroups])

  // Обработчик UPDATE события
  const handleUpdate = useCallback(
    (
      group: MapPointGroup,
      objectId: string,
      changedFields: Record<string, unknown> | null,
      modelInfo: ObjectModelInfo
    ) => {
      if (!changedFields) return

      setPointsData(prev => {
        const newData = new Map(prev)
        const records = newData.get(group.id) || []

        const recordIndex = records.findIndex(
          r => String(r[modelInfo.pkFieldName]) === String(objectId)
        )

        if (recordIndex !== -1) {
          // Обновить запись с новыми полями
          const updatedRecords = [...records]
          updatedRecords[recordIndex] = {
            ...records[recordIndex],
            ...changedFields, // Обновляем ВСЕ измененные поля
          }
          newData.set(group.id, updatedRecords)
        }

        return newData
      })

      const { latitudeField, longitudeField } = group.coordinateFields
      const coordsChanged = latitudeField in changedFields || longitudeField in changedFields

      if (!coordsChanged) {
        return
      }
    },
    [setPointsData]
  )

  // Обработчик CREATE события
  const handleCreate = useCallback(
    async (group: MapPointGroup, objectId: string, modelInfo: ObjectModelInfo) => {
      // Загрузить полные данные новой записи через команду из группы
      try {
        const commandParams = {
          objectCode: group.objectCode,
          commandCode: group.getDataCommand.name,
          method: group.getDataCommand.httpMethod,
          transformResponseWithPagination: true,
          // Фильтр по ID - нужно получить только эту запись
          filter: `${modelInfo.pkFieldName}==${objectId}`,
          size: 1,
        }

        const response = await executeCommand(commandParams).unwrap()

        // Извлечь данные (может быть в разных форматах в зависимости от transformResponseWithPagination)
        const records = response?.content || response?.data || response
        const newRecord: ObjectDataRecord | undefined = Array.isArray(records)
          ? records[0]
          : records

        if (!newRecord) {
          return
        }

        // 1. Добавить полные данные в состояние
        setPointsData(prev => {
          const newData = new Map(prev)
          const records = newData.get(group.id) || []

          // Проверить, что записи еще нет (защита от дубликатов)
          const exists = records.some(r => String(r[modelInfo.pkFieldName]) === String(objectId))
          if (!exists) {
            newData.set(group.id, [...records, newRecord])
          }

          return newData
        })
      } catch (error) {
        console.error(`Error point loading ${objectId}:`, error)
      }
    },
    [executeCommand, setPointsData]
  )

  // Обработчик DELETE события
  const handleDelete = useCallback(
    (group: MapPointGroup, objectId: string, modelInfo: ObjectModelInfo) => {
      setPointsData(prev => {
        const newData = new Map(prev)
        const records = newData.get(group.id) || []

        const filteredRecords = records.filter(
          r => String(r[modelInfo.pkFieldName]) !== String(objectId)
        )

        if (filteredRecords.length < records.length) {
          newData.set(group.id, filteredRecords)
        }

        return newData
      })
    },
    [setPointsData]
  )

  // Главный обработчик сообщений
  const handleMessage = useCallback(
    async (msg: IMessage) => {
      const body = parseMessageBody(msg.body)
      const { objectCode, objectId, changedFields, eventName } = body

      // Найти группы для этого objectCode
      const affectedGroups = groupsByObjectCode.get(objectCode)
      if (!affectedGroups || affectedGroups.length === 0) {
        return
      }

      // Получить модель и PK поле
      const modelInfo = objectModelsMap.get(objectCode)
      if (!modelInfo) {
        return
      }

      // Обработка событий для каждой затронутой группы
      for (const group of affectedGroups) {
        switch (eventName) {
          case EVENT_NAME_OBJECT_MESSAGE.UPDATE:
            handleUpdate(group, objectId, changedFields, modelInfo)
            break
          case EVENT_NAME_OBJECT_MESSAGE.CREATE:
            await handleCreate(group, objectId, modelInfo)
            break
          case EVENT_NAME_OBJECT_MESSAGE.DELETE:
            handleDelete(group, objectId, modelInfo)
            break
          default:
            console.log(`Unknown event type: ${eventName}`)
        }
      }
    },
    [groupsByObjectCode, objectModelsMap, handleUpdate, handleCreate, handleDelete]
  )

  // Подписка на WebSocket
  const [setIsSubscribed] = useSubscription(destinations, handleMessage, {})

  // Управление подпиской на основе готовности
  useEffect(() => {
    const shouldSubscribe = isMapReady && objectModelsMap.size > 0

    if (shouldSubscribe) {
      setIsSubscribed(true)
    } else {
      setIsSubscribed(false)
    }

    // Cleanup: отписаться при размонтировании
    return () => {
      setIsSubscribed(false)
    }
  }, [isMapReady, objectModelsMap.size, setIsSubscribed])
}
